import { useContext, createContext } from 'react';
import { requestFormById } from 'api/operations/formService';
import { runInAction, makeAutoObservable } from 'mobx';
import { getRootStoreInstance } from 'store/rootStore';
import { completeSubscriberChecklistStep, getRequiredSubInfo, getSubscriberById } from 'api/operations/electionService';
import { bpHistory } from 'global/bpHistory';
import { getEmployeeProfileURL } from 'global/checklists-helpers/SubscriberChecklistHelper';
import { getGroupById } from 'api/operations/groupService';
import { Group } from 'types/groupService';
import { ChecklistCompletionStatus, ChecklistOption } from 'types/enrollmentService';
import { SubChecklistCard } from 'types/electionService';
import SubscriberDelegate from 'store/modules/elections/subscriberDelegate';
import { FormObjectFromJSON } from '@insurancemenu/imenu-components-lib/dist/types/dynamicForms';
import { DynamicFormDelegate } from '@insurancemenu/imenu-components-lib/dist/utils/forms';

interface DomainData {
  subscriberId: string;
  // TaskId
  entityId: string;
  groupId: string;
}

export default class QuoteSubscriberProfilePageStore {
  rootStore = getRootStoreInstance();
  private _domainData: DomainData;
  private _subscriberDelegate: SubscriberDelegate | null = null;
  private _isInitialLoading = false;
  private _group: Pick<Group, 'name' | 'id' | 'contactInfo'> | null = null;

  public dynamicFormDelegate = runInAction(
    () =>
      new DynamicFormDelegate({
        currentUserRoles: this.rootStore.userStore.userRoles,
        fetchForm: async (formId: string): Promise<FormObjectFromJSON> => {
          return requestFormById(formId);
        },
        onSubmit: this.updatePrimaryInfo.bind(this),
        disableResetAfterSubmit: true,
      }),
  );

  public get isInitialLoading(): boolean {
    return this._isInitialLoading;
  }

  public get domainData(): DomainData {
    return this._domainData;
  }

  public get checklistOptions(): ChecklistOption<SubChecklistCard>[] {
    return this._subscriberDelegate?.getTaskChecklistOptions(this._domainData.entityId) || [];
  }

  public get isStepCompleted(): boolean {
    return (
      this.checklistOptions.find((option) => option.card === SubChecklistCard.QUOTING_SUBSCRIBER_PROFILE)
        ?.completionStatus === ChecklistCompletionStatus.COMPLETED
    );
  }

  public get group(): Pick<Group, 'name' | 'id' | 'contactInfo'> | null {
    return this._group;
  }

  private _setInitialLoading(loadingStatus: boolean): void {
    this._isInitialLoading = loadingStatus;
  }

  private _setGroup(group: Group): void {
    this._group = {
      name: group.name,
      id: group.id,
      contactInfo: group.contactInfo,
    };
  }

  private async _getSubscriber(): Promise<void> {
    const subscriber = await getSubscriberById(this._domainData.subscriberId);
    runInAction(() => {
      this._subscriberDelegate = new SubscriberDelegate({ subscriber, periodId: this._domainData.entityId });
    });
  }

  public async getInitialData(): Promise<void> {
    this._setInitialLoading(true);

    const domainId = this.rootStore.domainStoreV2.domainHeader.id;
    const requiredSubInfo = await getRequiredSubInfo(domainId);
    await runInAction(async () => {
      const primaryFormId = requiredSubInfo?.quotingSubFormId;

      const [group] = await Promise.all([getGroupById(this._domainData.groupId), this._getSubscriber()]);

      await runInAction(async () => {
        if (primaryFormId) {
          await this.dynamicFormDelegate.buildForm(primaryFormId, this._subscriberDelegate?.subscriber.common || {});
        }
        runInAction(() => {
          this._setGroup(group);
          this._setInitialLoading(false);
        });
      });
    });
  }

  public async updatePrimaryInfo(): Promise<void> {
    if (!this.dynamicFormDelegate.isFormValid) return;

    await this._subscriberDelegate?.updatePrimary(this.dynamicFormDelegate.formAnswers, this._domainData.entityId);
    await runInAction(async () => {
      await this._completeChecklistStep();
      this._setInitialLoading(false);
      this.returnBack();
    });
  }

  private async _completeChecklistStep(): Promise<void> {
    await completeSubscriberChecklistStep({
      subId: this._domainData.subscriberId,
      entityId: this._domainData.entityId,
      card: SubChecklistCard.QUOTING_SUBSCRIBER_PROFILE,
    });
  }

  public returnBack(): void {
    bpHistory.push(getEmployeeProfileURL({ groupId: this._domainData.groupId }));
  }

  constructor(domainData: DomainData) {
    this._domainData = domainData;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const QuoteSubscriberProfilePageStoreContext = createContext(
  new QuoteSubscriberProfilePageStore({
    groupId: 'stub-group-id',
    subscriberId: 'stub-subscriber-id',
    entityId: 'stub-entity-id',
  }),
);

export const useQuoteSubscriberProfilePageStore = (): QuoteSubscriberProfilePageStore => {
  return useContext(QuoteSubscriberProfilePageStoreContext);
};

export { QuoteSubscriberProfilePageStoreContext };
